import { RefObject, useEffect, useMemo } from 'react';
import { PlayerRef } from '@remotion/player';
import { FPS_24 } from '../../../constants';
import { CompositionConfig } from '../../types/CompositionConfig';
import { getPlayerConfigIntro } from '../utils/playerConfigGetters';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { updatePlayerStoreWithId } from '@/stores/playerV2';
import { roundToNDecimalPlaces } from '@/libs/utils';

export const useUpdateCurrentSrtIndex = ({
  playerConfig,
  remotionPlayerRef
}: {
  playerConfig: CompositionConfig | null;
  remotionPlayerRef: RefObject<PlayerRef>;
}) => {
  const transcriptStore = useTranscriptContext();
  const { clipId } = useClipsContext();
  const startTimesAtSrtIndex = useMemo(() => {
    if (transcriptStore.timeSrtIndexArray.length === 0) {
      return [];
    }
    return transcriptStore.timeSrtIndexArray.map(a => a.startTime);
  }, [transcriptStore.timeSrtIndexArray]);

  const intro = getPlayerConfigIntro(playerConfig);
  /**
   * Update the player srt index based on time and the srt index of the words
   */
  useEffect(() => {
    if (startTimesAtSrtIndex.length === 0) return;
    const player = remotionPlayerRef.current;
    const onPlayerTimeUpdate = ({ detail: { frame } }: { detail: { frame: number } }) => {
      const currentTime = roundToNDecimalPlaces(frame / FPS_24, 3);
      const introDuration = intro ? roundToNDecimalPlaces((intro.toFrame - intro.fromFrame) / FPS_24, 3) : 0;
      const mainSectionCurrentTime = currentTime - introDuration;
      const index = startTimesAtSrtIndex.findLastIndex(time => time <= mainSectionCurrentTime);

      updatePlayerStoreWithId(clipId, { currentSrtIndex: transcriptStore.timeSrtIndexArray[index]?.srtIndex });
    };
    if (player) {
      player.addEventListener('timeupdate', onPlayerTimeUpdate);
    }

    return () => {
      if (player) {
        player.removeEventListener('timeupdate', onPlayerTimeUpdate);
      }
    };
  }, [clipId, startTimesAtSrtIndex, transcriptStore.timeSrtIndexArray, remotionPlayerRef, intro]);
};
